import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { AccountDraft } from '../domain/account-draft.model';
import type { AccountSettingsError } from '../domain/account-settings-error';
import { initialState } from './account-settings.state';

/**
 * Account-settings slice. Reducers are pure state transitions only; the I/O
 * (the PATCH) lives in the saga, which dispatches these lifecycle transitions as
 * it progresses. The page seeds the draft with `hydrated` from the authenticated
 * user, and acknowledges a completed save with `saveAcknowledged` (after it has
 * refreshed `AuthContext` and notified) so a subsequent save can run.
 */
const slice = createSlice({
  name: 'accountSettings',
  initialState,
  reducers: {
    /** Seed the draft from the authenticated user (source of truth). */
    hydrated: (state, action: PayloadAction<AccountDraft>) => {
      state.draft = action.payload;
    },
    /** A single field changed; clears that field's validation error. */
    fieldChanged: (
      state,
      action: PayloadAction<{ field: keyof AccountDraft; value: string }>,
    ) => {
      const { field, value } = action.payload;
      state.draft[field] = value;
      delete state.validationErrors[field];
    },
    // ── Save lifecycle (dispatched by the saga) ──
    saveStarted: (state) => {
      state.saveStatus = 'saving';
      state.error = null;
      state.validationErrors = {};
    },
    saveSucceeded: (state) => {
      state.saveStatus = 'succeeded';
    },
    saveFailed: (state, action: PayloadAction<AccountSettingsError>) => {
      state.saveStatus = 'failed';
      state.error = action.payload;
      if (action.payload.kind === 'validation') {
        state.validationErrors = action.payload.fields;
      }
    },
    /** The page handled a successful save (refreshed + notified); back to idle. */
    saveAcknowledged: (state) => {
      state.saveStatus = 'idle';
    },
    // ── Profile photo lifecycle (#579, dispatched by the saga) ──
    avatarUploadStarted: (state) => {
      state.avatarStatus = 'uploading';
      state.avatarError = null;
    },
    avatarRemoveStarted: (state) => {
      state.avatarStatus = 'removing';
      state.avatarError = null;
    },
    avatarUploaded: (state) => {
      state.avatarStatus = 'uploaded';
    },
    avatarRemoved: (state) => {
      state.avatarStatus = 'removed';
    },
    avatarFailed: (state, action: PayloadAction<string>) => {
      state.avatarStatus = 'failed';
      state.avatarError = action.payload;
    },
    /** The page handled the outcome (refreshed + notified); back to idle. */
    avatarAcknowledged: (state) => {
      state.avatarStatus = 'idle';
      state.avatarError = null;
    },
    /** Reset everything to the initial state (on unmount / navigation away). */
    resetForm: () => initialState,
  },
});

export const accountSettingsActions = slice.actions;
export const accountSettingsReducer = slice.reducer;
